/**
 * queryPlanner.js
 *
 * Deterministic tool execution planner.
 * Maps intent + temporal + entity context → ExecutionPlan.
 *
 * Replaces the ad-hoc "isBroadQuery" logic in useKeviaChat with a clean,
 * intent-aware plan that specifies exactly which tools to run and whether
 * to synthesize + inject proactive context.
 *
 * Falls back to resolveDataIntent scoring for unrecognized patterns.
 */

import { resolveDataIntent }              from "./resolveDataIntent";
import { getDataToolById }                from "./toolSchemas";
import { isRoleAllowed, resolvePermissionScope } from "./permissionFilters";
import { detectBibleQuery }               from "../kevia-bible/bibleQueryDetector";

const MAX_PARALLEL_TOOLS = 3;

/**
 * @typedef {{ toolIds: string[], synthesize: boolean, proactive: boolean, bibleQuery?: { detected: boolean, reference: string|null, versions: string[]|null } }} ExecutionPlan
 */

function filterAllowed(toolIds, role) {
  return toolIds.filter((id) => {
    const schema = getDataToolById(id);
    return schema && isRoleAllowed(schema, role ?? "*");
  }).slice(0, MAX_PARALLEL_TOOLS);
}

function safePlan(toolIds, role, synthesize = false, proactive = false) {
  const allowed = filterAllowed(toolIds, role);
  if (allowed.length === 0) {
    const fallback = filterAllowed(["get_dashboard_snapshot"], role);
    return { toolIds: fallback, synthesize: true, proactive };
  }
  return { toolIds: allowed, synthesize, proactive };
}

/**
 * Build a deterministic execution plan for a message.
 *
 * @param {{
 *   intent: string,
 *   temporalIntent: string|null,
 *   entityContext: Object|null,
 *   currentModule: string,
 *   appScope: Object,
 *   signals?: Array,
 *   text?: string,
 * }} params
 * @returns {ExecutionPlan}
 */
export function buildExecutionPlan({
  intent,
  temporalIntent,
  entityContext,
  currentModule,
  appScope,
  signals = [],
  text = "",
}) {
  const scope = resolvePermissionScope(appScope);
  const role  = scope.role ?? "*";

  // ── Broad district overview ──────────────────────────────────────
  if (intent === "general" || intent === "analysis") {
    return safePlan(
      ["get_dashboard_snapshot", "get_church_activity_summary", "get_people_needing_followup"],
      role, true, true
    );
  }

  // ── Visit planning (temporal-aware) ──────────────────────────────
  if (intent === "visit_plan") {
    if (temporalIntent === "next")    return safePlan(["get_next_visit"],                              role);
    if (temporalIntent === "today")   return safePlan(["get_today_visits"],                            role);
    if (temporalIntent === "week")    return safePlan(["get_week_schedule", "get_upcoming_visits"],    role);
    if (temporalIntent === "pending") return safePlan(["get_upcoming_visits"],                         role);
    if (temporalIntent === "recent")  return safePlan(
      entityContext?.id ? ["get_visit_timeline"] : ["get_today_visits"],                               role);
    // Entity-scoped or default: today + upcoming
    if (entityContext?.id)            return safePlan(["get_visit_timeline", "get_today_visits"],      role);
    return safePlan(["get_today_visits", "get_upcoming_visits"],                                       role);
  }

  // ── Events ───────────────────────────────────────────────────────
  if (intent === "events") {
    return safePlan(["get_upcoming_events"], role);
  }

  // ── Planning ─────────────────────────────────────────────────────
  if (intent === "planning") {
    if (temporalIntent === "week")  return safePlan(["get_week_schedule", "get_upcoming_events"], role);
    if (temporalIntent === "today") return safePlan(["get_today_visits", "get_upcoming_events"],  role);
    // General planning → district-level overview with synthesis
    return safePlan(["get_dashboard_snapshot", "get_week_schedule"], role, true, true);
  }

  // ── Family / people followup ──────────────────────────────────────
  if (intent === "family_followup") {
    if (entityContext?.id && entityContext?.type === "family") {
      return safePlan(["get_family_overview", "get_visit_timeline"], role);
    }
    return safePlan(["get_people_needing_followup"], role);
  }

  // ── Sermon / reflection / bible — Bible RAG, no data tools ──────────
  if (intent === "sermon" || intent === "reflection" || intent === "bible") {
    const bibleQuery = detectBibleQuery(text, intent);
    return { toolIds: [], synthesize: false, proactive: false, bibleQuery };
  }

  // ── Prayer — include Bible RAG when a reference or keyword is present ─
  if (intent === "prayer") {
    const bibleQuery = detectBibleQuery(text, intent);
    const base = safePlan(["get_recent_prayers"], role);
    return bibleQuery.detected ? { ...base, bibleQuery } : base;
  }

  // ── Fallback: use resolveDataIntent scoring ───────────────────────
  const candidates = resolveDataIntent({
    intent, temporalIntent, currentModule, entityContext, appScope, signals,
  });
  const executable = candidates.filter((c) => c.shouldExecute).slice(0, 2);
  if (executable.length === 0) {
    return safePlan(["get_dashboard_snapshot"], role, true, false);
  }
  return {
    toolIds:   executable.map((c) => c.toolId),
    synthesize: executable.length > 1,
    proactive:  false,
  };
}
